import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { useLanguage } from '@contexts/LanguageContext';
import Icon from '@components/Icon/Icon';
import Button from '@components/common/Button';
import './ImageCropModal.scss';

const ImageCropModal = ({
    isOpen,
    onClose,
    onConfirm,
    attachment,
    isLoading = false
}) => {
    const { t } = useLanguage();
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [aspect, setAspect] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [croppedArea, setCroppedArea] = useState(null);

    const getImageUrl = () => {
        const apiUrl = process.env.REACT_APP_API_URL;
        return `${apiUrl}/api/files/serve/${attachment.file.file_path}`;
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
        setCroppedArea(croppedArea);
    }, []);

    const handleSubmit = () => {
        if (croppedAreaPixels && onConfirm) {
            const cropData = {
                crop: croppedAreaPixels,  // These are the pixel coordinates on the original image
                rotation,
                zoom,
                aspect
            };
            onConfirm(cropData);
        }
    };

    const handleCancel = () => {
        if (!isLoading && onClose) {
            onClose();
        }
    };

    const resetCrop = () => {
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
    };

    const adjustZoom = (delta) => {
        setZoom(prev => Math.max(1, Math.min(3, prev + delta)));
    };

    const adjustRotation = (delta) => {
        setRotation(prev => Math.max(-180, Math.min(180, prev + delta)));
    };

    const aspectRatios = [
        { label: t('attachments.crop.aspectRatio.square'), value: 1 },
        { label: t('attachments.crop.aspectRatio.landscape'), value: 4 / 3 },
        { label: t('attachments.crop.aspectRatio.portrait'), value: 3 / 4 },
        { label: t('attachments.crop.aspectRatio.wide'), value: 16 / 9 },
    ];

    if (!isOpen || !attachment) {
        return null;
    }

    return (
        <div className="modal-overlay" onClick={handleCancel}>
            <div className="image-crop-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{t('attachments.crop.title')}</h3>
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isLoading}
                        className="close-btn"
                        title={t('common.close')}
                    >
                        <Icon icon="times" />
                    </button>
                </div>

                <div className="modal-content">
                    <div className="crop-container">
                        <Cropper
                            image={getImageUrl()}
                            crop={crop}
                            zoom={zoom}
                            rotation={rotation}
                            aspect={aspect}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                            onRotationChange={setRotation}
                            showGrid={true}
                            zoomWithScroll={true}
                            restrictPosition={false}
                        />
                    </div>

                    <div className="crop-controls">
                        <div className="control-group">
                            <label>
                                {t('attachments.crop.zoom')} <span className="value">{zoom.toFixed(1)}x</span>
                            </label>
                            <div className="control-input-wrapper">
                                <button
                                    type="button"
                                    onClick={() => adjustZoom(-0.1)}
                                    disabled={zoom <= 1}
                                    className="control-btn"
                                    title="Decrease zoom"
                                >
                                    <Icon icon="minus" />
                                </button>
                                <input
                                    type="range"
                                    value={zoom}
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                                    className="slider"
                                />
                                <button
                                    type="button"
                                    onClick={() => adjustZoom(0.1)}
                                    disabled={zoom >= 3}
                                    className="control-btn"
                                    title="Increase zoom"
                                >
                                    <Icon icon="plus" />
                                </button>
                            </div>
                        </div>

                        <div className="control-group">
                            <label>
                                {t('attachments.crop.rotation')} <span className="value">{rotation}°</span>
                            </label>
                            <div className="control-input-wrapper">
                                <button
                                    type="button"
                                    onClick={() => adjustRotation(-1)}
                                    disabled={rotation <= -180}
                                    className="control-btn"
                                    title="Rotate left 1°"
                                >
                                    <Icon icon="minus" />
                                </button>
                                <input
                                    type="range"
                                    value={rotation}
                                    min={-180}
                                    max={180}
                                    step={1}
                                    onChange={(e) => setRotation(parseInt(e.target.value))}
                                    className="slider"
                                />
                                <button
                                    type="button"
                                    onClick={() => adjustRotation(1)}
                                    disabled={rotation >= 180}
                                    className="control-btn"
                                    title="Rotate right 1°"
                                >
                                    <Icon icon="plus" />
                                </button>
                            </div>
                        </div>

                        <div className="control-group">
                            <label>{t('attachments.crop.aspectRatio.label')}</label>
                            <select
                                value={aspect}
                                onChange={(e) => setAspect(parseFloat(e.target.value))}
                                className="aspect-select"
                            >
                                {aspectRatios.map((ratio) => (
                                    <option key={ratio.value} value={ratio.value}>
                                        {ratio.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <Button
                        onClick={handleCancel}
                        variant="secondary"
                        disabled={isLoading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="primary"
                        disabled={isLoading || !croppedAreaPixels}
                        loading={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <Icon icon="spinner" spin />
                                {t('attachments.crop.inProgress')}
                            </>
                        ) : (
                            <>
                                <Icon icon="crop" />
                                {t('attachments.crop.apply')}
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ImageCropModal;
